import {
	Check,
	Clock,
	Copy,
	Eye,
	EyeOff,
	RefreshCw,
	Star,
	Trash2,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import Toast from "@/components/_shared/Toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { generateTOTP } from "./totpUtils";

interface HistoryEntry {
	secret: string;
	timestamp: number;
}

interface PinnedEntry {
	name: string;
	secret: string;
}

interface TOTPFormProps {
	initialCode?: string;
}

export default function TOTPForm({ initialCode = "" }: TOTPFormProps) {
	const [secret, setSecret] = useState("");
	const [code, setCode] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [timeLeft, setTimeLeft] = useState(30);
	const [history, setHistory] = useState<HistoryEntry[]>([]);
	const [showSecretIdx, setShowSecretIdx] = useState<number | null>(null);
	const [showPinnedSecretIdx, setShowPinnedSecretIdx] = useState<number | null>(
		null,
	);
	const [pinned, setPinned] = useState<PinnedEntry[]>(() => {
		try {
			const raw = localStorage.getItem("totp_pinned");
			return raw ? JSON.parse(raw) : [];
		} catch {
			return [];
		}
	});
	const [pinPrompt, setPinPrompt] = useState(false);
	const [pinName, setPinName] = useState("");
	const pinSecretRef = useRef<string>("");
	const [successMessage, setSuccessMessage] = useState<string | null>(null);
	const [copiedCode, setCopiedCode] = useState<string | null>(null);

	useEffect(() => {
		let timer: NodeJS.Timeout;
		let active = true;
		async function update() {
			if (!secret.trim()) {
				setCode("");
				setTimeLeft(30);
				return;
			}
			const now = Date.now();
			const step = 30;
			const epoch = Math.floor(now / 1000);
			const timeStep = Math.floor(epoch / step);
			const nextStep = (timeStep + 1) * step * 1000;
			setTimeLeft(Math.max(0, Math.floor((nextStep - now) / 1000)));
			setCode(await generateTOTP(secret, now));
		}
		update();
		timer = setInterval(update, 1000);
		return () => {
			active = false;
			clearInterval(timer);
		};
	}, [secret]);

	useEffect(() => {
		if (successMessage) {
			setSuccessMessage((prev) => {
				if (prev) return null;
				return successMessage;
			});
			const timer = setTimeout(() => {
				setSuccessMessage(null);
			}, 3000);
			return () => clearTimeout(timer);
		}
	}, [successMessage]);

	useEffect(() => {
		if (copiedCode) {
			const timer = setTimeout(() => {
				setCopiedCode(null);
			}, 2000);
			return () => clearTimeout(timer);
		}
	}, [copiedCode]);

	useEffect(() => {
		if (initialCode) {
			setSecret(initialCode);
			setSuccessMessage("TOTP-Code aus URL übernommen");
		}
	}, [initialCode]);

	const copyToClipboard = (codeToCopy: string) => {
		if (!codeToCopy || codeToCopy === "------" || codeToCopy === "ERROR")
			return;

		navigator.clipboard
			.writeText(codeToCopy)
			.then(() => {
				setCopiedCode(codeToCopy);
				setSuccessMessage("Code in Zwischenablage kopiert");
			})
			.catch((err) => {
				console.error("Fehler beim Kopieren:", err);
				setSuccessMessage("Fehler beim Kopieren");
			});
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);
		if (!secret.trim()) {
			setError("Bitte ein TOTP-Secret eingeben.");
			return;
		}
		setHistory((h) => [{ secret, timestamp: Date.now() }, ...h.slice(0, 9)]);
		setSuccessMessage("TOTP zum Verlauf hinzugefügt");
	};

	const handlePin = () => {
		setPinPrompt(true);
		pinSecretRef.current = secret;
		setPinName("");
	};

	const confirmPin = () => {
		if (!pinName.trim()) return;
		const newPinned = [
			...pinned,
			{ name: pinName.trim(), secret: pinSecretRef.current },
		];
		setPinned(newPinned);
		localStorage.setItem("totp_pinned", JSON.stringify(newPinned));
		setPinPrompt(false);
		setSuccessMessage(`"${pinName.trim()}" wurde angeheftet`);
	};

	const unpin = (idx: number) => {
		const name = pinned[idx].name;
		const newPinned = pinned.filter((_, i) => i !== idx);
		setPinned(newPinned);
		localStorage.setItem("totp_pinned", JSON.stringify(newPinned));
		setSuccessMessage(`"${name}" wurde entfernt`);
	};

	const getTimeLeftColor = () => {
		const percent = timeLeft / 30;
		if (percent > 0.7) return "bg-emerald-500";
		if (percent > 0.4) return "bg-amber-500";
		return "bg-rose-500";
	};

	return (
		<div className="flex flex-col gap-8">
			{/* Success message toast */}
			<Toast
				message={successMessage || ""}
				onClose={() => setSuccessMessage(null)}
				visible={!!successMessage}
			/>

			{/* OBERER BEREICH: Live-Generierung und angeheftete Codes */}
			<section className="card rounded-xl border border-gray-100 p-6 shadow-sm backdrop-blur-sm dark:border-gray-700 dark:bg-gray-800/70">
				<h2 className="mb-4 border-gray-100 border-b pb-2 font-semibold text-gray-800 text-xl dark:border-gray-700 dark:text-gray-100">
					TOTP-Code generieren
				</h2>
				<form
					className="mb-6 flex flex-col items-end gap-4 md:flex-row"
					onSubmit={handleSubmit}
				>
					<div className="flex-1">
						<Label
							className="mb-2 block font-medium text-gray-600 dark:text-gray-300"
							htmlFor="totp-secret"
						>
							TOTP-Secret (Base32)
						</Label>
						<Input
							autoComplete="off"
							className="border-gray-200 bg-white transition-all focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:focus:ring-blue-600"
							id="totp-secret"
							onChange={(e) => setSecret(e.target.value)}
							placeholder="z.B. JBSWY3DPEHPK3PXP"
							value={secret}
						/>
					</div>
					<div className="flex w-full gap-2 md:w-auto">
						<Button
							className="w-full transition-all hover:bg-blue-100 md:w-auto dark:hover:bg-blue-900/30"
							type="submit"
							variant="secondary"
						>
							<RefreshCw className="mr-2 h-4 w-4" />
							In Verlauf
						</Button>
						<Button
							className="w-full border-blue-200 text-blue-600 hover:bg-blue-50 md:w-auto dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-900/20"
							onClick={handlePin}
							type="button"
							variant="outline"
						>
							<Star className="mr-2 h-4 w-4" />
							Anheften
						</Button>
					</div>
				</form>
				{error && (
					<div className="mb-4 rounded border border-rose-100 bg-rose-50 p-2 text-rose-500 text-sm dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-400">
						{error}
					</div>
				)}

				<div className="mb-6 flex flex-col items-center gap-6 rounded-lg border border-gray-100 bg-gray-50 p-4 md:flex-row dark:border-gray-700 dark:bg-gray-800">
					<button
						className={`flex items-center gap-2 rounded-lg px-2 py-1 font-mono font-semibold text-4xl tracking-widest transition-all ${
							code && code !== "------" && code !== "ERROR"
								? "group cursor-pointer text-gray-900 hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-700"
								: "cursor-not-allowed text-gray-400 dark:text-gray-500"
						}`}
						disabled={!code || code === "------" || code === "ERROR"}
						onClick={() => copyToClipboard(code)}
					>
						{code || "------"}
						{copiedCode === code ? (
							<Check className="h-5 w-5 text-emerald-500 opacity-100 transition-opacity dark:text-emerald-400" />
						) : (
							<Copy className="h-5 w-5 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100 dark:text-gray-500" />
						)}
					</button>
					<div className="flex flex-col items-center gap-1 md:ml-auto">
						<div className="h-3 w-40 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
							<div
								className={`h-full ${getTimeLeftColor()} transition-all duration-1000 ease-linear`}
								style={{ width: `${(timeLeft / 30) * 100}%` }}
							/>
						</div>
						<span className="font-medium text-gray-600 text-sm tabular-nums dark:text-gray-300">
							{timeLeft}s
						</span>
					</div>
				</div>

				{/* Angeheftete Codes */}
				{pinned.length > 0 && (
					<div className="mt-6">
						<h3 className="mb-3 font-semibold text-gray-700 text-sm uppercase tracking-wider dark:text-gray-200">
							Angeheftete TOTP-Codes
						</h3>
						<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
							{pinned.map((entry, idx) => (
								<div
									className="flex flex-col rounded-lg border border-gray-100 bg-white p-4 shadow-sm transition-shadow hover:shadow-md dark:border-gray-700 dark:bg-gray-800"
									key={idx}
								>
									<div className="mb-2 truncate font-bold text-blue-600 text-lg dark:text-blue-400">
										{entry.name}
									</div>
									<PinnedTOTP
										isCopied={!!copiedCode && copiedCode === code}
										onCopy={copyToClipboard}
										secret={entry.secret}
									/>
									<div className="mt-auto flex justify-between border-gray-100 border-t pt-2 dark:border-gray-700">
										<Button
											className="text-gray-600 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
											onClick={() =>
												setShowPinnedSecretIdx(
													showPinnedSecretIdx === idx ? null : idx,
												)
											}
											size="sm"
											type="button"
											variant="ghost"
										>
											{showPinnedSecretIdx === idx ? (
												<EyeOff className="h-4 w-4" />
											) : (
												<Eye className="h-4 w-4" />
											)}
											<span className="ml-1">Secret</span>
										</Button>
										<Button
											className="text-gray-600 hover:text-rose-600 dark:text-gray-400 dark:hover:text-rose-400"
											onClick={() => unpin(idx)}
											size="sm"
											type="button"
											variant="ghost"
										>
											<Trash2 className="h-4 w-4" />
											<span className="ml-1">Entfernen</span>
										</Button>
									</div>
									{showPinnedSecretIdx === idx && (
										<div className="mt-2 break-all rounded border border-gray-100 bg-gray-50 p-2 font-mono text-xs dark:border-gray-700 dark:bg-gray-900/50">
											{entry.secret}
										</div>
									)}
								</div>
							))}
						</div>
					</div>
				)}
			</section>

			{/* UNTERER BEREICH: Verlauf */}
			<section className="card rounded-xl border border-gray-100 p-6 shadow-sm backdrop-blur-sm dark:border-gray-700 dark:bg-gray-800/70">
				<h3 className="mb-4 border-gray-100 border-b pb-2 font-semibold text-gray-700 text-lg dark:border-gray-700 dark:text-gray-200">
					Verlauf{" "}
					<span className="font-normal text-gray-500 text-sm dark:text-gray-400">
						(letzte 10, nur diese Session)
					</span>
				</h3>

				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
					{history.length === 0 && (
						<div className="col-span-full rounded-lg border border-gray-100 bg-gray-50 p-4 text-center text-gray-400 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-500">
							Noch keine Einträge. Generiere TOTP-Codes, um sie hier anzuzeigen.
						</div>
					)}

					{history.map((entry, idx) => (
						<div
							className="flex flex-col rounded-lg border border-gray-100 bg-white p-4 shadow-sm transition-shadow hover:shadow-md dark:border-gray-700 dark:bg-gray-800"
							key={idx}
						>
							<div className="mb-2 flex items-center text-gray-500 text-sm dark:text-gray-400">
								<Clock className="mr-1 h-4 w-4" />
								{new Date(entry.timestamp).toLocaleTimeString()}
							</div>
							<PinnedTOTP
								isCopied={!!copiedCode && copiedCode === code}
								onCopy={copyToClipboard}
								secret={entry.secret}
							/>
							<div className="mt-auto flex justify-between border-gray-100 border-t pt-2 dark:border-gray-700">
								<Button
									className="text-gray-600 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
									onClick={() => {
										pinSecretRef.current = entry.secret;
										setPinPrompt(true);
										setPinName("");
									}}
									size="sm"
									type="button"
									variant="ghost"
								>
									<Star className="mr-1 h-4 w-4" />
									Anheften
								</Button>
								<Button
									className="text-gray-600 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
									onClick={() =>
										setShowSecretIdx(showSecretIdx === idx ? null : idx)
									}
									size="sm"
									type="button"
									variant="ghost"
								>
									{showSecretIdx === idx ? (
										<EyeOff className="mr-1 h-4 w-4" />
									) : (
										<Eye className="mr-1 h-4 w-4" />
									)}
									Secret
								</Button>
							</div>
							{showSecretIdx === idx && (
								<div className="mt-2 break-all rounded border border-gray-100 bg-gray-50 p-2 font-mono text-xs dark:border-gray-700 dark:bg-gray-900/50">
									{entry.secret}
								</div>
							)}
						</div>
					))}
				</div>
			</section>

			{/* Pin-Dialog */}
			{pinPrompt && (
				<div className="fade-in fixed inset-0 z-50 flex animate-in items-center justify-center bg-black/60 backdrop-blur-sm">
					<div className="zoom-in-95 w-full max-w-sm animate-in rounded-xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-800">
						<h4 className="mb-3 font-semibold text-gray-800 text-lg dark:text-gray-100">
							TOTP-Code anheften
						</h4>
						<p className="mb-4 text-gray-500 text-sm dark:text-gray-400">
							Gib einen Namen für den angehefteten TOTP-Code ein. Angeheftete
							Codes bleiben auch nach dem Neuladen der Seite erhalten.
						</p>
						<Label
							className="mb-2 block text-gray-600 dark:text-gray-300"
							htmlFor="pin-name"
						>
							Name
						</Label>
						<Input
							autoFocus
							className="mb-4 border-gray-200 bg-white focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:focus:ring-blue-600"
							id="pin-name"
							onChange={(e) => setPinName(e.target.value)}
							value={pinName}
						/>
						<div className="flex justify-end gap-3">
							<Button
								className="text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
								onClick={() => setPinPrompt(false)}
								type="button"
								variant="ghost"
							>
								Abbrechen
							</Button>
							<Button
								className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800"
								disabled={!pinName.trim()}
								onClick={confirmPin}
								type="button"
							>
								<Star className="mr-2 h-4 w-4" />
								Anheften
							</Button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

// Live TOTP code for pinned entries with circular progress indicator
function PinnedTOTP({
	secret,
	onCopy,
	isCopied,
}: {
	secret: string;
	onCopy: (code: string) => void;
	isCopied: boolean;
}) {
	const [code, setCode] = useState<string>("------");
	const [timeLeft, setTimeLeft] = useState(30);

	useEffect(() => {
		let timer: NodeJS.Timeout;
		let active = true;

		async function update() {
			if (!secret.trim()) {
				setCode("------");
				return;
			}

			try {
				const now = Date.now();
				const step = 30;
				const epoch = Math.floor(now / 1000);
				const timeStep = Math.floor(epoch / step);
				const nextStep = (timeStep + 1) * step * 1000;
				setTimeLeft(Math.max(0, Math.floor((nextStep - now) / 1000)));

				setCode(await generateTOTP(secret));
			} catch (error) {
				console.error("Error generating TOTP for pinned:", error);
				setCode("ERROR");
			}
		}

		update();
		timer = setInterval(update, 1000);

		return () => {
			active = false;
			clearInterval(timer);
		};
	}, [secret]);

	// Calculate stroke-dashoffset for circular timer
	const calculateOffset = () => {
		const circumference = 2 * Math.PI * 18; // 2πr where r is 18
		return circumference * (1 - timeLeft / 30);
	};

	// Color for the timer
	const getTimerColor = () => {
		if (timeLeft > 20) return "stroke-emerald-500 dark:stroke-emerald-400";
		if (timeLeft > 10) return "stroke-amber-500 dark:stroke-amber-400";
		return "stroke-rose-500 dark:stroke-rose-400";
	};

	return (
		<div className="mb-3 flex items-center justify-between">
			<button
				className={`flex items-center gap-2 rounded-lg px-2 py-1 font-mono font-semibold text-2xl tracking-widest transition-all ${
					code && code !== "------" && code !== "ERROR"
						? "group cursor-pointer text-gray-900 hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-700"
						: "cursor-not-allowed text-gray-400 dark:text-gray-500"
				}`}
				disabled={!code || code === "------" || code === "ERROR"}
				onClick={() => onCopy(code)}
			>
				{code}
				{isCopied ? (
					<Check className="h-4 w-4 text-emerald-500 opacity-100 transition-opacity dark:text-emerald-400" />
				) : (
					<Copy className="h-4 w-4 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100 dark:text-gray-500" />
				)}
			</button>

			<div className="relative flex h-10 w-10 items-center justify-center">
				{/* Background circle */}
				<svg className="-rotate-90 h-10 w-10 transform" viewBox="0 0 40 40">
					<circle
						className="stroke-gray-200 dark:stroke-gray-700"
						cx="20"
						cy="20"
						fill="none"
						r="18"
						strokeWidth="3"
					/>

					{/* Timer circle */}
					<circle
						className={`transition-all duration-1000 ease-linear ${getTimerColor()}`}
						cx="20"
						cy="20"
						fill="none"
						r="18"
						strokeDasharray={2 * Math.PI * 18}
						strokeDashoffset={calculateOffset()}
						strokeLinecap="round"
						strokeWidth="3"
					/>
				</svg>

				{/* Time text */}
				<span className="absolute inset-0 flex items-center justify-center font-medium text-xs tabular-nums">
					{timeLeft}
				</span>
			</div>
		</div>
	);
}
